import axios from 'axios'
import Router from 'next/router'
import { destroyCookie, parseCookies } from 'nookies'
import { toast } from 'react-toastify'

import {
	reAuthenticateByRefreshToken,
	clear,
	getDefaultCookieOptions
} from './auth'
import { cache } from './cache'

import { queryClient } from 'providers/QueryClientProvider'

const API_BASE_URL = process.env.API

const api = axios.create({
	adapter: cache.adapter,
	baseURL: API_BASE_URL,
	headers: {
		'Content-Type': 'application/json'
	},
	withCredentials: true
})

let abortController = new AbortController()

api.interceptors.request.use((config) => {
	// Possivelmente pode remover essa linha seguinte mas é válido esperar, pois não sei oq esse comando faz
	config.signal = abortController.signal

	// fluxo do TOTP segue inalterado
	const vittudeTotp = parseCookies()['vittude-totp']
	if (vittudeTotp) {
		config.headers['X-Vittude-Totp'] = vittudeTotp
	}

	return config
})

let isForcingLogout = false
let isRefreshing = false
let failedQueue = []

const processQueue = (error, tokenRefreshed = false) => {
	failedQueue.forEach((prom) => {
		if (tokenRefreshed) {
			prom.resolve()
		} else {
			prom.reject(error)
		}
	})
	failedQueue = []
}

const forceLogout = async () => {
	if (isForcingLogout) return

	isForcingLogout = true
	cancelAllRequests()

	toast.warning('Sua sessão expirou. Por favor, faça login novamente.', {
		autoClose: 5000,
		closeButton: true,
		hideProgressBar: false
	})

	await queryClient.invalidateQueries({ queryKey: ['get-me'] })
	clear()

	Router.replace('/entrar')
	setTimeout(() => {
		isForcingLogout = false
		isRefreshing = false
		failedQueue = []
	}, 3000)
}

api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config
		const errorLogCode = error.response?.data?.log_content?.error_code
		const cookieOptions = getDefaultCookieOptions()
		const isTokenEndpoint = [`${API_BASE_URL}/token/`].includes(
			originalRequest.url
		)

		if (isForcingLogout) {
			cancelAllRequests()
			return
		}

		if (errorLogCode === 'mfa_required') {
			destroyCookie(null, 'vittude-totp', cookieOptions)
			Router.push('/autenticacao')
			return Promise.reject(error)
		}

		// Token expirado
		if (error?.response?.status === 401 && !isTokenEndpoint) {
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({
						resolve: () => resolve(api(originalRequest)),
						reject
					})
				})
			}

			// Inicia tentativa de reautenticar através do refresh-token
			isRefreshing = true
			try {
				const refreshResponse = await reAuthenticateByRefreshToken()

				if (refreshResponse.ok) {
					processQueue(null, true)
					return api(originalRequest)
				}
			} catch (refreshError) {
				console.error('Erro ao tentar refresh:', refreshError)
			} finally {
				isRefreshing = false
			}

			processQueue(new Error('Refresh token inválido'), false)
			await forceLogout()
			return
		}

		return Promise.reject(error)
	}
)

export const cancelAllRequests = () => {
	abortController.abort()
	abortController = new AbortController()
}

export default api
